import { useState, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, Save, Link2 } from 'lucide-react';
import type { VcfCapability, VcfActivity, VcfGuardrail, ActivityPhase } from '@/hooks/useWorkstreams';
import type { FrameworkChangeItem, FrameworkEntityType } from '@/hooks/useFrameworkChanges';
import type { ResourceRole } from '@/hooks/useAdminRoles';
import { useFrameworkDeliveryPlan, useSaveFrameworkDeliveryPlan } from '@/hooks/useFrameworkDeliveryPlan';
import { useSkills } from '@/hooks/useSkills';
import { generateWeekAllocations } from '@/lib/generateAllocations';
import {
  PHASE_COLOURS as ADMIN_PHASE_COLOURS,
  getSkillColours as adminGetSkillColours,
  getRoleColours as adminGetRoleColours,
} from '@/components/delivery/constants';
import { useActivityDependencies, useAddDependency, useDeleteDependency } from '@/hooks/useVcfAdmin';
import { cn } from '@/lib/utils';

const NUM_WEEKS = 12;
const DAYS_PER_WEEK = 5;

interface ActivityAllocationColumnProps {
  selectedCapability: VcfCapability | null;
  // Edit mode props
  isEditMode: boolean;
  changeSetId: string | null;
  pendingActivityMap: Map<string, FrameworkChangeItem>;
  pendingGuardrailMap: Map<string, FrameworkChangeItem>;
  recordChange: (input: { entityType: FrameworkEntityType; entityId: string | null; changeType: 'create' | 'update' | 'delete'; beforeJson?: string; afterJson?: string }) => Promise<void>;
  undoChange: (entityType: FrameworkEntityType, entityId: string) => Promise<void>;
  // Activity editing callbacks
  setEditingActivity: (a: VcfActivity | null) => void;
  setShowAddActivity: (b: boolean) => void;
  setEditingGuardrail: (g: VcfGuardrail | null) => void;
  setShowAddGuardrail: (b: boolean) => void;
  handleDeleteEntity: (entityType: string, entityId: string, name: string, entity: unknown) => void;
  roles: ResourceRole[];
}

interface AllocationState {
  allocations: Map<string, Map<number, number>>;
  activityBaseDays: Map<string, number>;
  capabilityStartWeek: number;
}

export function ActivityAllocationColumn({
  selectedCapability,
  isEditMode,
  changeSetId,
  pendingActivityMap: _pendingActivityMap,
  pendingGuardrailMap,
  recordChange,
  undoChange: _undoChange,
  setEditingActivity,
  setShowAddActivity,
  setEditingGuardrail,
  setShowAddGuardrail,
  handleDeleteEntity,
  roles,
}: ActivityAllocationColumnProps) {
  const { data: planData } = useFrameworkDeliveryPlan();
  const { data: skillsData } = useSkills();
  const savePlan = useSaveFrameworkDeliveryPlan();
  const skillsList = skillsData?.skills || [];

  const [guardrailsExpanded, setGuardrailsExpanded] = useState(false);
  const [allocState, setAllocState] = useState<AllocationState>({
    allocations: new Map(),
    activityBaseDays: new Map(),
    capabilityStartWeek: 1,
  });
  const [hasAllocChanges, setHasAllocChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [depPopoverActivityId, setDepPopoverActivityId] = useState<string | null>(null);

  // Initialize allocation state from server data for selected capability
  useEffect(() => {
    if (!planData || !selectedCapability) return;

    const allocations = new Map<string, Map<number, number>>();
    const activityBaseDays = new Map<string, number>();

    for (const act of selectedCapability.activities || []) {
      activityBaseDays.set(act.id, act.baseDays);
    }
    for (const act of planData.activities || []) {
      if (activityBaseDays.has(act.activityId)) {
        activityBaseDays.set(act.activityId, act.baseDays);
      }
    }
    for (const alloc of planData.allocations || []) {
      if (!activityBaseDays.has(alloc.activityId)) continue;
      if (!allocations.has(alloc.activityId)) {
        allocations.set(alloc.activityId, new Map());
      }
      allocations.get(alloc.activityId)!.set(alloc.week, alloc.days / 100);
    }

    const startWeek = planData.capabilityStartWeeks?.find(
      (r) => r.capabilityId === selectedCapability.id
    )?.startWeek ?? 1;

    setAllocState({ allocations, activityBaseDays, capabilityStartWeek: startWeek });
    setHasAllocChanges(false);
  }, [planData, selectedCapability?.id]);

  const getWeekAllocation = useCallback((activityId: string, week: number): number => {
    return allocState.allocations.get(activityId)?.get(week) ?? 0;
  }, [allocState.allocations]);

  const getActivityBaseDays = useCallback((act: VcfActivity): number => {
    // Prefer the activity's own baseDays (includes pending change set updates)
    // over the stale allocState copy. allocState is only used for allocation-specific overrides.
    return act.baseDays;
  }, []);

  const updateWeekAllocation = useCallback((activityId: string, week: number, days: number) => {
    setAllocState((prev) => {
      const allocations = new Map(prev.allocations);
      if (!allocations.has(activityId)) allocations.set(activityId, new Map());
      const actMap = new Map(allocations.get(activityId)!);
      if (days > 0) actMap.set(week, days); else actMap.delete(week);
      allocations.set(activityId, actMap);
      return { ...prev, allocations };
    });
    setHasAllocChanges(true);
  }, []);

  const updateStartWeek = useCallback((startWeek: number) => {
    setAllocState((prev) => ({ ...prev, capabilityStartWeek: startWeek }));
    setHasAllocChanges(true);
  }, []);

  const handleGenerate = useCallback(() => {
    if (!selectedCapability) return;
    if (!window.confirm('Generate will auto-schedule week allocations from base days and dependencies. Continue?')) return;

    const acts = (selectedCapability.activities || []).map((act) => ({
      id: act.id,
      role: act.role,
      skill: act.skill || '',
      adjustedDays: getActivityBaseDays(act) / 100,
      phase: act.activityPhase as ActivityPhase | undefined,
      sortOrder: act.sortOrder,
      predecessors: act.dependencies?.map((d) => ({
        predecessorId: d.predecessorId,
        type: d.dependencyType as 'FS' | 'SS' | 'FF',
        lagDays: d.lagDays,
      })),
    }));
    const result = generateWeekAllocations(acts, allocState.capabilityStartWeek, DAYS_PER_WEEK, NUM_WEEKS);
    setAllocState((prev) => {
      const newAllocs = new Map(prev.allocations);
      for (const act of selectedCapability.activities || []) {
        const weekMap = new Map<number, number>();
        for (const [w, d] of Object.entries(result.allocations[act.id] || {})) {
          weekMap.set(Number(w), d);
        }
        newAllocs.set(act.id, weekMap);
      }
      return { ...prev, allocations: newAllocs };
    });
    setHasAllocChanges(true);
  }, [selectedCapability, getActivityBaseDays, allocState.capabilityStartWeek]);

  const handleSaveAllocations = useCallback(async () => {
    if (!selectedCapability || isSaving) return;
    setIsSaving(true);
    try {
      if (isEditMode && changeSetId) {
        // Record allocation changes through the change set workflow
        for (const act of selectedCapability.activities || []) {
          const weekMap = allocState.allocations.get(act.id);
          if (weekMap && weekMap.size > 0) {
            const weekAllocations: Record<string, number> = {};
            weekMap.forEach((days, week) => { weekAllocations[week.toString()] = days; });
            await recordChange({
              entityType: 'allocation',
              entityId: act.id,
              changeType: 'update',
              afterJson: JSON.stringify({ activityId: act.id, weekAllocations }),
            });
          }
        }
        // Record start week change
        await recordChange({
          entityType: 'start_week',
          entityId: selectedCapability.id,
          changeType: 'update',
          afterJson: JSON.stringify({ capabilityId: selectedCapability.id, startWeek: allocState.capabilityStartWeek }),
        });
        setHasAllocChanges(false);
      } else {
        // Direct save (when not in edit mode)
        const allocations: Array<{ activityId: string; weekAllocations: Record<string, number> }> = [];
        for (const act of selectedCapability.activities || []) {
          const weekMap = allocState.allocations.get(act.id);
          if (weekMap && weekMap.size > 0) {
            const weekAllocations: Record<string, number> = {};
            weekMap.forEach((days, week) => { weekAllocations[week.toString()] = days; });
            allocations.push({ activityId: act.id, weekAllocations });
          }
        }
        await savePlan.mutateAsync({
          allocations,
          capabilityStartWeeks: [{ capabilityId: selectedCapability.id, startWeek: allocState.capabilityStartWeek }],
          changeDescription: `Updated allocations for ${selectedCapability.name}`,
        });
        setHasAllocChanges(false);
      }
    } catch (err) {
      console.error('Failed to save:', err);
      window.alert('Failed to save allocations.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedCapability, allocState, isSaving, savePlan, isEditMode, changeSetId, recordChange]);

  if (!selectedCapability) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 text-sm py-8">
        Select a capability to view activities and allocations
      </div>
    );
  }

  const activities = selectedCapability.activities || [];
  const totalDays = activities.reduce((sum, act) => sum + getActivityBaseDays(act), 0) / 100;

  return (
    <div className="space-y-4">
      {/* Capability header — name is already in the breadcrumb, so just show stats */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 truncate">{selectedCapability.name}</h3>
        <span className="text-xs text-gray-500 flex-shrink-0">{activities.length} activities · {totalDays.toFixed(1)}d</span>
      </div>

      {/* Controls bar — only shown in edit mode */}
      {isEditMode && (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {hasAllocChanges && (
              <span className="text-xs text-amber-600 font-medium">● Unsaved</span>
            )}
            <label className="text-xs text-gray-500">Start:</label>
            <select
              value={allocState.capabilityStartWeek}
              onChange={(e) => updateStartWeek(parseInt(e.target.value, 10))}
              className="text-xs border border-gray-300 rounded px-1 py-0.5"
            >
              {Array.from({ length: NUM_WEEKS }, (_, i) => (
                <option key={i} value={i + 1}>W{i + 1}</option>
              ))}
            </select>
            <button onClick={handleGenerate} className="btn-secondary btn-sm text-xs">Generate</button>
            <button
              onClick={() => setShowAddActivity(true)}
              className="btn-secondary btn-sm text-xs flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> Add Activity
            </button>
            {hasAllocChanges && (
              <button onClick={handleSaveAllocations} disabled={isSaving} className="btn-primary btn-sm text-xs flex items-center gap-1">
                <Save className="h-3 w-3" /> {isSaving ? 'Saving\u2026' : 'Save'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Timeline (week-based) — collapsible on mobile, always open on desktop */}
      {selectedCapability && (
        <>
          {/* Desktop: always visible */}
          <div className="hidden md:block card overflow-hidden !p-0">
            <div className="p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Timeline</div>
              <WeekTimeline activities={activities} getWeekAllocation={getWeekAllocation} numWeeks={NUM_WEEKS} startWeek={allocState.capabilityStartWeek} />
            </div>
          </div>
          {/* Mobile: collapsible */}
          <details className="md:hidden card overflow-hidden !p-0">
            <summary className="px-3 py-2 flex items-center justify-between cursor-pointer text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              <span>{'\u25B8'} Timeline</span>
              <span className="text-[10px] font-normal normal-case tracking-normal text-gray-400">tap to expand</span>
            </summary>
            <div className="p-4">
              <WeekTimeline activities={activities} getWeekAllocation={getWeekAllocation} numWeeks={NUM_WEEKS} startWeek={allocState.capabilityStartWeek} />
            </div>
          </details>
        </>
      )}

      {/* Inline dependency editor — when depPopoverActivityId is set */}
      {depPopoverActivityId && selectedCapability && (() => {
        const act = activities.find(a => a.id === depPopoverActivityId);
        if (!act) return null;
        return (
          <DependencySection
            activityId={act.id}
            activityName={act.name}
            activities={activities}
            capabilityId={selectedCapability.id}
            onClose={() => setDepPopoverActivityId(null)}
          />
        );
      })()}

      {/* Mobile activity cards */}
      <div className="md:hidden card overflow-hidden !p-0 divide-y divide-gray-100">
        {activities.map((act) => {
          const baseDays = getActivityBaseDays(act);
          const skillColour = adminGetSkillColours(act.skill, skillsList);
          const roleColour = adminGetRoleColours(act.role, roles);
          const phase = act.activityPhase;
          const deps = act.dependencies || [];

          return (
            <div key={act.id} className={cn("p-3", depPopoverActivityId === act.id && "bg-blue-50")}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-900">
                    {!!act.isMilestone && <span className="text-amber-500">{'\u25C6'} </span>}
                    {act.name}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {phase && ADMIN_PHASE_COLOURS[phase] && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[7px] font-bold uppercase tracking-wider"
                        style={{ backgroundColor: ADMIN_PHASE_COLOURS[phase].bg, color: ADMIN_PHASE_COLOURS[phase].text }}
                      >
                        {ADMIN_PHASE_COLOURS[phase].label}
                      </span>
                    )}
                    {act.skill && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase"
                        style={{ backgroundColor: skillColour.bg, color: skillColour.text }}
                      >
                        {act.skill}
                      </span>
                    )}
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase"
                      style={{ backgroundColor: roleColour.bg, color: roleColour.text }}
                    >
                      {act.role}
                    </span>
                    <span className="text-xs font-mono text-xtravirt-cyan font-semibold">{(baseDays / 100).toFixed(1)}d</span>
                  </div>
                  {deps.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {deps.map(d => {
                        const pred = activities.find(a => a.id === d.predecessorId);
                        return (
                          <span key={d.id || d.predecessorId} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 text-[9px] text-gray-600">
                            <span className="font-bold text-sky-600">{d.dependencyType}</span>
                            <span className="truncate max-w-[100px]">{pred?.name || '...'}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {isEditMode && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setDepPopoverActivityId(depPopoverActivityId === act.id ? null : act.id)}
                      className={cn("p-1.5 rounded text-gray-400 bg-gray-100", depPopoverActivityId === act.id && "text-blue-600 bg-blue-100")}
                    >
                      <Link2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setEditingActivity(act)} className="p-1.5 rounded text-gray-400 bg-gray-100">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDeleteEntity('activity', act.id, act.name, act)} className="p-1.5 rounded text-gray-400 bg-gray-100">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              {/* Week allocation cells as flex grid */}
              <div className="flex flex-wrap gap-[3px] mt-2">
                {Array.from({ length: NUM_WEEKS }, (_, i) => {
                  const week = i + 1;
                  const days = getWeekAllocation(act.id, week);
                  return (
                    <div key={i} className="text-center">
                      <div className="text-[7px] text-gray-400">W{week}</div>
                      <div className={cn(
                        "w-7 h-[22px] border rounded flex items-center justify-center text-[10px]",
                        days > 0
                          ? "border-sky-300 bg-sky-100 text-sky-700 font-bold"
                          : "border-gray-200 bg-gray-50"
                      )}>
                        {days > 0 ? days : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Allocation table */}
      <div className="hidden md:block card overflow-hidden !p-0">
        <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="text-left min-w-[140px]">Activity</th>
              <th className="text-left w-16">Phase</th>
              <th className="text-left w-16">Skill</th>
              <th className="text-left w-24">Role</th>
              <th className="text-center w-14">Base</th>
              {Array.from({ length: NUM_WEEKS }, (_, i) => (
                <th key={i} className="dp-week-header">W{i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activities.map((act) => {
              const baseDays = getActivityBaseDays(act);
              const skillColour = adminGetSkillColours(act.skill, skillsList);
              const roleColour = adminGetRoleColours(act.role, roles);
              const phase = act.activityPhase;
              const deps = act.dependencies || [];

              return (
                <tr
                  key={act.id}
                  className={cn('group', depPopoverActivityId === act.id && 'bg-blue-50')}
                >
                  <td className="relative">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-1.5 min-w-0">
                        {!!act.isMilestone && <span className="text-amber-500 text-xs mt-0.5">{'\u25C6'}</span>}
                        <div className="min-w-0">
                          <div className="dp-activity-name truncate max-w-[280px]">{act.name}</div>
                          {/* Dependency chips */}
                          {deps.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {deps.map(d => {
                                const pred = activities.find(a => a.id === d.predecessorId);
                                return (
                                  <span key={d.id || d.predecessorId} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 text-[9px] text-gray-600">
                                    <span className="font-bold text-sky-600">{d.dependencyType}</span>
                                    <span className="truncate max-w-[120px]">{pred?.name || '...'}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      {isEditMode && (
                        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDepPopoverActivityId(depPopoverActivityId === act.id ? null : act.id);
                            }}
                            className={cn("p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50", depPopoverActivityId === act.id && "text-blue-600 bg-blue-50")}
                            title="Dependencies"
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setEditingActivity(act); }} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteEntity('activity', act.id, act.name, act); }} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {phase && ADMIN_PHASE_COLOURS[phase] && (
                      <span
                        className="inline-block px-1 py-0.5 rounded text-[7px] font-bold uppercase tracking-wider"
                        style={{ backgroundColor: ADMIN_PHASE_COLOURS[phase].bg, color: ADMIN_PHASE_COLOURS[phase].text }}
                      >
                        {ADMIN_PHASE_COLOURS[phase].label}
                      </span>
                    )}
                  </td>
                  <td>
                    {act.skill && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase"
                        style={{ backgroundColor: skillColour.bg, color: skillColour.text }}
                      >
                        {act.skill}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase"
                      style={{ backgroundColor: roleColour.bg, color: roleColour.text }}
                    >
                      {act.role}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className="text-xs font-mono">{(baseDays / 100).toFixed(1)}</span>
                  </td>
                  {Array.from({ length: NUM_WEEKS }, (_, i) => {
                    const week = i + 1;
                    const days = getWeekAllocation(act.id, week);
                    return (
                      <td key={i} className="dp-week-cell">
                        {isEditMode ? (
                          <input
                            type="number"
                            value={days || ''}
                            onChange={(e) => updateWeekAllocation(act.id, week, parseFloat(e.target.value) || 0)}
                            className={cn('dp-week-input', days > 0 && 'dp-cell-filled')}
                            step="0.5"
                            min="0"
                            max={DAYS_PER_WEEK}
                          />
                        ) : (
                          days > 0 ? <span className="inline-flex items-center justify-center w-8 h-6 rounded-md bg-sky-50 text-sky-700 text-xs font-semibold">{days}</span> : null
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Guardrails Section */}
      <div className="card overflow-hidden !p-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setGuardrailsExpanded(!guardrailsExpanded)}
            className="flex items-center gap-1.5 font-medium text-gray-900 hover:text-gray-700 transition-colors"
          >
            {guardrailsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Guardrails ({selectedCapability.guardrails?.length || 0})
          </button>
          {isEditMode ? (
            <button
              onClick={() => setShowAddGuardrail(true)}
              className="flex items-center gap-1 text-xs text-xtravirt-cyan hover:text-xtravirt-navy"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          ) : (
            <span className="text-[10px] uppercase tracking-wide text-gray-400">Read-only</span>
          )}
        </div>
        {guardrailsExpanded && (
          <div className="border-t border-gray-200 px-4 py-3">
        {selectedCapability.guardrails?.length === 0 ? (
          <p className="text-sm text-gray-500">No guardrails defined</p>
        ) : (
          <div className="space-y-2">
            {selectedCapability.guardrails?.map((gr) => {
              const grItem = pendingGuardrailMap.get(gr.id);
              const grPendingDelete = grItem?.changeType === 'delete';
              const grPendingCreate = grItem?.changeType === 'create';
              const grPendingUpdate = grItem?.changeType === 'update';
              return (
                <div
                  key={gr.id}
                  className={cn(
                    'p-3 rounded-lg border bg-gray-50 group',
                    !grPendingCreate && !grPendingUpdate && !grPendingDelete && 'border-gray-200',
                    grPendingCreate && 'border-green-300 bg-green-50',
                    grPendingUpdate && 'border-amber-300 bg-amber-50',
                    grPendingDelete && 'border-red-300 bg-red-50',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={cn('font-medium text-gray-900', grPendingDelete && 'line-through text-red-700')}>{gr.name}</span>
                      {grPendingCreate && <span className="text-[9px] uppercase font-bold bg-green-200 text-green-900 px-1.5 py-0.5 rounded">New</span>}
                      {grPendingUpdate && <span className="text-[9px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">Modified</span>}
                      {grPendingDelete && <span className="text-[9px] uppercase font-bold bg-red-200 text-red-900 px-1.5 py-0.5 rounded">Pending delete</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-xs text-gray-600">{gr.code}</span>
                      {isEditMode && !grPendingDelete && (
                        <>
                          <button onClick={() => setEditingGuardrail(gr)} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600" title="Edit">
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button onClick={() => handleDeleteEntity('guardrail', gr.id, gr.name, gr)} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600" title="Delete">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    {gr.baselineValue != null && <span>Baseline: {gr.baselineValue}</span>}
                    <span>{'\u00B7'} Factor: {gr.multiplierFactor != null ? `+${gr.multiplierFactor}x` : '\u2014'} {gr.multiplierLabel || ''}</span>
                    {gr.multiplierStep != null && gr.multiplierStep > 1 && <span>{'\u00B7'} Step: {gr.multiplierStep}</span>}
                    <span>{'\u00B7'} Type: {gr.inputType}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        )}
      </div>

      {/* Scope Content */}
      <div className="card overflow-hidden !p-0">
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Scope Content</h4>
          {!isEditMode && <span className="text-[10px] uppercase tracking-wide text-gray-400">Read-only</span>}
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="label">Scope of Work</label>
            {isEditMode ? (
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[60px] focus:outline-none focus:border-xtravirt-cyan focus:ring-1 focus:ring-xtravirt-cyan"
                value={selectedCapability.scopeText || ''}
                onChange={(e) => {
                  recordChange({
                    entityType: 'capability',
                    entityId: selectedCapability.id,
                    changeType: 'update',
                    afterJson: JSON.stringify({ scopeText: e.target.value }),
                  });
                }}
                placeholder="Describe the scope of work..."
              />
            ) : (
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 min-h-[40px]">
                {selectedCapability.scopeText || <span className="text-gray-400 italic">No scope text defined</span>}
              </p>
            )}
          </div>
          <div>
            <label className="label">Outcomes / Deliverables</label>
            {isEditMode ? (
              <div>
                {(selectedCapability.deliverables || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <span className="text-green-500">{'\u2713'}</span>
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                      value={d}
                      onChange={(e) => {
                        const updated = [...(selectedCapability.deliverables || [])];
                        updated[i] = e.target.value;
                        recordChange({
                          entityType: 'capability',
                          entityId: selectedCapability.id,
                          changeType: 'update',
                          afterJson: JSON.stringify({ deliverables: updated }),
                        });
                      }}
                    />
                    <button
                      onClick={() => {
                        const updated = (selectedCapability.deliverables || []).filter((_, idx) => idx !== i);
                        recordChange({
                          entityType: 'capability',
                          entityId: selectedCapability.id,
                          changeType: 'update',
                          afterJson: JSON.stringify({ deliverables: updated }),
                        });
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >{'\u00d7'}</button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const updated = [...(selectedCapability.deliverables || []), ''];
                    recordChange({
                      entityType: 'capability',
                      entityId: selectedCapability.id,
                      changeType: 'update',
                      afterJson: JSON.stringify({ deliverables: updated }),
                    });
                  }}
                  className="text-xs text-xtravirt-cyan hover:text-xtravirt-navy mt-1"
                >+ Add deliverable</button>
              </div>
            ) : (
              selectedCapability.deliverables && selectedCapability.deliverables.length > 0 ? (
                <ul className="mt-1 space-y-1">
                  {selectedCapability.deliverables.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-green-500 mt-0.5">{'\u2713'}</span> {d}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400 italic mt-1">No deliverables defined</p>
              )
            )}
          </div>
          <div>
            <label className="label">Customer Prerequisites</label>
            {isEditMode ? (
              <div>
                {(selectedCapability.prerequisites || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <span className="text-amber-500">{'\u2022'}</span>
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                      value={p}
                      onChange={(e) => {
                        const updated = [...(selectedCapability.prerequisites || [])];
                        updated[i] = e.target.value;
                        recordChange({
                          entityType: 'capability',
                          entityId: selectedCapability.id,
                          changeType: 'update',
                          afterJson: JSON.stringify({ prerequisites: updated }),
                        });
                      }}
                    />
                    <button
                      onClick={() => {
                        const updated = (selectedCapability.prerequisites || []).filter((_, idx) => idx !== i);
                        recordChange({
                          entityType: 'capability',
                          entityId: selectedCapability.id,
                          changeType: 'update',
                          afterJson: JSON.stringify({ prerequisites: updated }),
                        });
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >{'\u00d7'}</button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const updated = [...(selectedCapability.prerequisites || []), ''];
                    recordChange({
                      entityType: 'capability',
                      entityId: selectedCapability.id,
                      changeType: 'update',
                      afterJson: JSON.stringify({ prerequisites: updated }),
                    });
                  }}
                  className="text-xs text-xtravirt-cyan hover:text-xtravirt-navy mt-1"
                >+ Add prerequisite</button>
              </div>
            ) : (
              selectedCapability.prerequisites && selectedCapability.prerequisites.length > 0 ? (
                <ul className="mt-1 space-y-1">
                  {selectedCapability.prerequisites.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-amber-500 mt-0.5">{'\u2022'}</span> {p}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400 italic mt-1">No prerequisites defined</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Inline Dependency Section ============

function DependencySection({
  activityId,
  activityName,
  activities,
  capabilityId,
  onClose,
}: {
  activityId: string;
  activityName: string;
  activities: VcfActivity[];
  capabilityId: string;
  onClose: () => void;
}) {
  const depsQuery = useActivityDependencies(capabilityId, activityId);
  const addDep = useAddDependency();
  const deleteDep = useDeleteDependency();
  const currentDeps: Array<{ id: string; activityId: string; predecessorId: string; dependencyType: string; lagDays: number }> = depsQuery.data?.dependencies || [];

  const handleToggle = (siblingId: string) => {
    const existing = currentDeps.find(d => d.predecessorId === siblingId);
    if (existing) {
      deleteDep.mutate({ capabilityId, activityId, dependencyId: existing.id });
    } else {
      addDep.mutate({ capabilityId, activityId, predecessorId: siblingId, dependencyType: 'FS', lagDays: 0 });
    }
  };

  const handleRemove = (depId: string) => {
    deleteDep.mutate({ capabilityId, activityId, dependencyId: depId });
  };

  const handleChangeType = (dep: typeof currentDeps[0], newType: string) => {
    deleteDep.mutate({ capabilityId, activityId, dependencyId: dep.id }, {
      onSuccess: () => {
        addDep.mutate({ capabilityId, activityId, predecessorId: dep.predecessorId, dependencyType: newType as 'FS' | 'SS' | 'FF', lagDays: dep.lagDays });
      },
    });
  };

  const handleChangeLag = (dep: typeof currentDeps[0], newLag: number) => {
    deleteDep.mutate({ capabilityId, activityId, dependencyId: dep.id }, {
      onSuccess: () => {
        addDep.mutate({ capabilityId, activityId, predecessorId: dep.predecessorId, dependencyType: dep.dependencyType as 'FS' | 'SS' | 'FF', lagDays: newLag });
      },
    });
  };

  return (
    <div className="border border-blue-200 rounded-lg bg-blue-50/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-blue-900 flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Dependencies for: {activityName}
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">Which activities must finish before this one starts?</p>
        </div>
        <button onClick={onClose} className="px-3 py-1 rounded-md bg-white border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-50">
          Done
        </button>
      </div>
      <div className="space-y-1.5">
        {activities.map(sibling => {
          if (sibling.id === activityId) return (
            <div key={sibling.id} className="flex items-center gap-3 px-3 py-2 rounded-md opacity-30">
              <span className="w-[18px] h-[18px] rounded border-2 border-gray-300 bg-gray-200 flex items-center justify-center text-[10px] text-gray-500">{'\u2014'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{sibling.name}</div>
                <div className="text-[10px] text-gray-400 italic">This activity</div>
              </div>
            </div>
          );
          const dep = currentDeps.find(d => d.predecessorId === sibling.id);
          const isChecked = !!dep;
          return (
            <div key={sibling.id} className={cn("flex flex-col px-3 py-2 rounded-md border", isChecked ? "bg-green-50 border-green-200" : "bg-white border-gray-200 hover:border-blue-200 cursor-pointer")}>
              <div className="flex items-center gap-3" onClick={() => handleToggle(sibling.id)}>
                <span className={cn("w-[18px] h-[18px] rounded border-2 flex items-center justify-center text-[10px] font-bold flex-shrink-0", isChecked ? "bg-green-500 border-green-600 text-white" : "border-gray-300 bg-white cursor-pointer")}>
                  {isChecked ? '\u2713' : ''}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-700 truncate">{sibling.name}</div>
                  <div className="text-[10px] text-gray-400">{(sibling.baseDays / 100).toFixed(1)}d {'\u00B7'} {sibling.role} {'\u00B7'} {sibling.activityPhase || '\u2014'}</div>
                </div>
                {isChecked && (
                  <button onClick={(e) => { e.stopPropagation(); handleRemove(dep!.id); }} className="text-gray-300 hover:text-red-500 text-lg leading-none">{'\u00D7'}</button>
                )}
              </div>
              {isChecked && dep && (
                <div className="flex items-center gap-2 mt-1.5 ml-[30px]">
                  <label className="text-[10px] text-gray-500">Type:</label>
                  <select value={dep.dependencyType} onChange={(e) => handleChangeType(dep, e.target.value)} className="text-[10px] border border-gray-300 rounded px-1.5 py-0.5">
                    <option value="FS">FS</option><option value="SS">SS</option><option value="FF">FF</option>
                  </select>
                  <label className="text-[10px] text-gray-500">Lag:</label>
                  <input type="number" value={dep.lagDays} onChange={(e) => handleChangeLag(dep, parseInt(e.target.value) || 0)} className="text-[10px] border border-gray-300 rounded px-1.5 py-0.5 w-10 text-center" min="0" />
                  <span className="text-[9px] text-gray-400">days</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Timeline Content ============

/** Week-based Gantt timeline using the allocation data directly */
const PHASE_COLORS: Record<string, string> = {
  workshop: '#3b82f6', assess: '#a855f7', design: '#eab308',
  implement: '#22c55e', validate: '#ef4444', document: '#7c3aed', kt: '#14b8a6',
};

/** Day-granular timeline with weekly grid. Bars show exact day positions within weeks. */
function WeekTimeline({
  activities,
  getWeekAllocation,
  numWeeks,
  daysPerWeek = 5,
  startWeek = 1,
}: {
  activities: VcfActivity[];
  getWeekAllocation: (activityId: string, week: number) => number;
  numWeeks: number;
  daysPerWeek?: number;
  startWeek?: number;
}) {
  // Find the last week that has any allocation
  let lastWeek = 1;
  for (const act of activities) {
    for (let w = numWeeks; w >= 1; w--) {
      if (getWeekAllocation(act.id, w) > 0) {
        lastWeek = Math.max(lastWeek, w);
        break;
      }
    }
  }
  const displayWeeks = Math.max(lastWeek + 1, 4);
  const totalDays = displayWeeks * daysPerWeek;

  if (activities.length === 0) return null;

  // Compute bar positions respecting dependencies + start week offset.
  // For each activity, start position = max(predecessor end positions) + start week offset.
  // End position = start + total allocated days.
  const startWeekOffset = (startWeek - 1) * daysPerWeek; // e.g., startWeek=2 → offset=5 days
  const actEndDays: Record<string, number> = {}; // activityId -> endDay (absolute)

  const activityBars = activities.map((act, idx) => {
    let totalAllocDays = 0;
    for (let w = 1; w <= numWeeks; w++) {
      totalAllocDays += getWeekAllocation(act.id, w);
    }

    if (totalAllocDays <= 0) {
      return { idx, act, startDay: 0, endDay: 0, barColor: '#6b7280' };
    }

    // Start day = start week offset + max of predecessor end days (respects FS + start week)
    let startDay = startWeekOffset;
    const deps = act.dependencies || [];
    for (const dep of deps) {
      const predEnd = actEndDays[dep.predecessorId] || startWeekOffset;
      startDay = Math.max(startDay, predEnd);
    }

    const endDay = startDay + totalAllocDays;
    actEndDays[act.id] = endDay;

    const phase = act.activityPhase;
    const barColor = (phase && PHASE_COLORS[phase]) || '#6b7280';

    return { idx, act, startDay, endDay, barColor };
  });

  return (
    <div>
      {/* Week headers */}
      <div className="flex items-end mb-0.5">
        <div className="w-32 md:w-48 flex-shrink-0" />
        <div className="flex-1 flex">
          {Array.from({ length: displayWeeks }, (_, i) => (
            <div key={i} className="flex-1 text-center text-[10px] font-semibold text-gray-500 border-l border-gray-200 first:border-l-0">
              W{i + 1}
            </div>
          ))}
        </div>
      </div>
      {/* Activity bars */}
      <div className="space-y-0.5">
        {activityBars.map(({ idx, act, startDay, endDay, barColor }) => (
          <div key={act.id} className="flex items-center h-[22px]">
            <div className="w-32 md:w-48 flex-shrink-0 text-[10px] md:text-xs text-gray-600 truncate pr-2">
              <span className="text-gray-400">{idx + 1}.</span> {act.name}
            </div>
            <div className="flex-1 h-[20px] relative">
              {/* Week grid lines */}
              <div className="absolute inset-0 flex">
                {Array.from({ length: displayWeeks }, (_, i) => (
                  <div key={i} className="flex-1 border-l border-gray-200 first:border-l-0 relative">
                    {/* Day sub-gridlines */}
                    {Array.from({ length: daysPerWeek - 1 }, (_, d) => (
                      <div
                        key={d}
                        className="absolute top-0 bottom-0 border-l border-gray-50"
                        style={{ left: `${((d + 1) / daysPerWeek) * 100}%` }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              {/* Activity bar */}
              {endDay > startDay && (
                <div
                  className="absolute top-[3px] h-[14px] rounded-sm"
                  style={{
                    left: `${(startDay / totalDays) * 100}%`,
                    width: `${Math.max(((endDay - startDay) / totalDays) * 100, 0.5)}%`,
                    backgroundColor: barColor,
                    opacity: 0.85,
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[9px] text-gray-400">
        {Object.entries(PHASE_COLORS).map(([p, color]) => (
          <span key={p} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: color }} />
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}
